export type PlatformAccountType =
  | "FACEBOOK"
  | "INSTAGRAM"
  | "GOOGLE"
  | "GOOGLE_SOCIAL"
  | "GOOGLE_ANALYTICS"
  | "TIKTOK"
  | "TIKTOK_SOCIAL"
  | "SNAPCHAT"
  | "LINKEDIN"
  | "BING_ADS"
  | "SPOTIFY"
  | "OPENAI_ADS";

export type PlatformConnectionUi = {
  brandName: string;
  networkName: string;
  logo: string;
  badgeLogo: string;
  connectLabel: string;
  title: string;
  subtitle: string;
  connectedSubtitle: string;
  securityText: string;
  /** Platforms that can continue without selecting an ad account */
  adAccountOptional: boolean;
  supportsProfileImage: boolean;
  storeImagePlatform?: "facebook" | "instagram" | "tiktok" | "linkedin" | "google";
};

export const SAMPLE_PROFILE_IMAGE = "images/global/sample-profile.png";

export const PLATFORM_CONNECTION_UI: Record<
  PlatformAccountType,
  PlatformConnectionUi
> = {
  FACEBOOK: {
    brandName: "Meta",
    networkName: "Facebook",
    logo: "images/global/meta.png",
    badgeLogo: "images/global/facebook.png",
    connectLabel: "Connect with Meta",
    title: "Connect your Meta account",
    subtitle:
      "Choose the Facebook profile and ad account you’d like to continue with.",
    connectedSubtitle:
      "Choose the Facebook profile and ad account you’d like to continue with.",
    securityText:
      "Authentication is handled securely by Meta. Your password is never shared with us.",
    adAccountOptional: true,
    supportsProfileImage: true,
    storeImagePlatform: "facebook",
  },
  INSTAGRAM: {
    brandName: "Meta",
    networkName: "Instagram",
    logo: "images/global/meta.png",
    badgeLogo: "images/global/instagram.png",
    connectLabel: "Connect with Meta",
    title: "Connect your Meta account",
    subtitle:
      "Choose the Instagram profile and ad account you’d like to continue with.",
    connectedSubtitle:
      "Choose the Instagram profile and ad account you’d like to continue with.",
    securityText:
      "Authentication is handled securely by Meta. Your password is never shared with us.",
    adAccountOptional: true,
    supportsProfileImage: true,
    storeImagePlatform: "instagram",
  },
  GOOGLE: {
    brandName: "Google",
    networkName: "Google",
    logo: "images/global/google-icon.png",
    badgeLogo: "images/global/google-icon.png",
    connectLabel: "Connect with Google",
    title: "Connect your Google account",
    subtitle:
      "Choose the Google profile and ad account you’d like to continue with.",
    connectedSubtitle:
      "Choose the Google profile and ad account you’d like to continue with.",
    securityText:
      "Authentication is handled securely by Google. Your password is never shared with us.",
    adAccountOptional: true,
    supportsProfileImage: true,
    storeImagePlatform: "google",
  },
  GOOGLE_SOCIAL: {
    brandName: "YouTube",
    networkName: "YouTube",
    logo: "images/global/youtube.png",
    badgeLogo: "images/global/youtube.png",
    connectLabel: "Connect with YouTube",
    title: "Connect your YouTube channel",
    subtitle: "Choose the Google / YouTube profile you’d like to use for social posts.",
    connectedSubtitle:
      "Your YouTube channel is connected for social posting.",
    securityText:
      "Authentication is handled securely by Google. Your password is never shared with us.",
    adAccountOptional: true,
    supportsProfileImage: true,
    storeImagePlatform: "google",
  },
  GOOGLE_ANALYTICS: {
    brandName: "Google Analytics",
    networkName: "Google Analytics",
    logo: "images/global/google_analytics.png",
    badgeLogo: "images/global/google_analytics.png",
    connectLabel: "Connect with Google Analytics",
    title: "Connect your Google Analytics account",
    subtitle: "Choose the Google Analytics account you’d like to continue with.",
    connectedSubtitle:
      "Choose the Google Analytics account you’d like to continue with.",
    securityText:
      "Authentication is handled securely by Google. Your password is never shared with us.",
    adAccountOptional: false,
    supportsProfileImage: false,
  },
  TIKTOK: {
    brandName: "TikTok",
    networkName: "TikTok",
    logo: "images/global/tiktok.png",
    badgeLogo: "images/global/tiktok.png",
    connectLabel: "Connect with TikTok",
    title: "Connect your TikTok account",
    subtitle:
      "Choose the TikTok profile and ad account you’d like to continue with.",
    connectedSubtitle:
      "Choose the TikTok profile and ad account you’d like to continue with.",
    securityText:
      "Authentication is handled securely by TikTok. Your password is never shared with us.",
    adAccountOptional: true,
    supportsProfileImage: false,
  },
  TIKTOK_SOCIAL: {
    brandName: "TikTok",
    networkName: "TikTok",
    logo: "images/global/tiktok.png",
    badgeLogo: "images/global/tiktok.png",
    connectLabel: "Connect with TikTok",
    title: "Connect your TikTok account",
    subtitle: "Choose your TikTok profile to continue.",
    connectedSubtitle: "Choose your TikTok profile to continue.",
    securityText:
      "Authentication is handled securely by TikTok. Your password is never shared with us.",
    adAccountOptional: true,
    supportsProfileImage: true,
    storeImagePlatform: "tiktok",
  },
  SNAPCHAT: {
    brandName: "Snapchat",
    networkName: "Snapchat",
    logo: "images/global/snapchat.png",
    badgeLogo: "images/global/snapchat.png",
    connectLabel: "Connect with Snapchat",
    title: "Connect your Snapchat account",
    subtitle:
      "Choose the Snapchat profile and ad account you’d like to continue with.",
    connectedSubtitle:
      "Choose the Snapchat profile and ad account you’d like to continue with.",
    securityText:
      "Authentication is handled securely by Snapchat. Your password is never shared with us.",
    adAccountOptional: false,
    supportsProfileImage: false,
  },
  LINKEDIN: {
    brandName: "LinkedIn",
    networkName: "LinkedIn",
    logo: "images/global/linkedin.png",
    badgeLogo: "images/global/linkedin.png",
    connectLabel: "Connect with LinkedIn",
    title: "Connect your LinkedIn account",
    subtitle:
      "Choose the LinkedIn profile and ad account you’d like to continue with.",
    connectedSubtitle:
      "Choose the LinkedIn profile and ad account you’d like to continue with.",
    securityText:
      "Authentication is handled securely by LinkedIn. Your password is never shared with us.",
    adAccountOptional: true,
    supportsProfileImage: true,
    storeImagePlatform: "linkedin",
  },
  BING_ADS: {
    brandName: "Bing",
    networkName: "Microsoft Advertising",
    logo: "images/global/bing.png",
    badgeLogo: "images/global/bing.png",
    connectLabel: "Connect with Bing",
    title: "Connect your Bing account",
    subtitle:
      "Choose the Bing profile and ad account you’d like to continue with.",
    connectedSubtitle:
      "Choose the Bing profile and ad account you’d like to continue with.",
    securityText:
      "Authentication is handled securely by Microsoft. Your password is never shared with us.",
    adAccountOptional: false,
    supportsProfileImage: false,
  },
  SPOTIFY: {
    brandName: "Spotify",
    networkName: "Spotify",
    logo: "images/global/spotify-logo.png",
    badgeLogo: "images/global/spotify-logo.png",
    connectLabel: "Connect with Spotify",
    title: "Connect your Spotify account",
    subtitle:
      "Choose the Spotify profile and ad account you’d like to continue with.",
    connectedSubtitle:
      "Choose the Spotify profile and ad account you’d like to continue with.",
    securityText:
      "Authentication is handled securely by Spotify. Your password is never shared with us.",
    adAccountOptional: false,
    supportsProfileImage: false,
  },
  OPENAI_ADS: {
    brandName: "ChatGPT Ads",
    networkName: "ChatGPT Ads",
    logo: "images/svgs/OpenAI_ad.png",
    badgeLogo: "images/svgs/OpenAI_ad.png",
    connectLabel: "Connect ChatGPT Ads",
    title: "Connect your ChatGPT Ads account",
    subtitle: "Enter your API key to securely connect your account.",
    connectedSubtitle: "Your ChatGPT Ads account is connected.",
    securityText: "Your API key is encrypted and securely stored.",
    adAccountOptional: false,
    supportsProfileImage: false,
  },
};

export const getPlatformConnectionUi = (
  type?: string,
): PlatformConnectionUi => {
  if (type && type in PLATFORM_CONNECTION_UI) {
    return PLATFORM_CONNECTION_UI[type as PlatformAccountType];
  }

  return PLATFORM_CONNECTION_UI.GOOGLE;
};

const isUsableProfileImageUrl = (url?: string | null) => {
  if (!url || typeof url !== "string") return false;
  const trimmed = url.trim();
  if (!trimmed || trimmed === "null" || trimmed === "undefined") return false;
  // Ignore local brand assets mistakenly stored as profile images
  if (
    /images\/global\//i.test(trimmed) &&
    !/sample-profile/i.test(trimmed)
  ) {
    return false;
  }
  return true;
};

export const getProfileImageFromDetails = (
  details: any = {},
  options?: { allowLogoUrl?: boolean },
) => {
  const candidates = [
    details?.profileImageUrl,
    details?.userImage,
    details?.youtubeProfileImageUrl,
    options?.allowLogoUrl === false ? null : details?.logo_url,
    details?.picture?.data?.url,
  ];

  for (const candidate of candidates) {
    if (isUsableProfileImageUrl(candidate)) {
      return candidate as string;
    }
  }

  return "";
};

export const getAccountInitial = (name?: string) => {
  const trimmed = (name || "").trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : "A";
};

export const AVATAR_COLORS = [
  "#3B82F6",
  "#10B981",
  "#8B5CF6",
  "#F59E0B",
  "#EF4444",
  "#06B6D4",
  "#EC4899",
  "#6366F1",
];

export const getAvatarColor = (seed?: string) => {
  const value = seed || "A";
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};
